// Hook de captura de un clip de audio para reconocimiento (Req. 12.1–12.2).
//
// Encapsula el ciclo micrófono → grabación → PCM base64 que exige Shazam
// (44100 Hz, mono, 16-bit LE), para reutilizarlo en el "Radar Acústico"
// (`AudioView`) y en el modo "Sonido" de la búsqueda del catálogo (`CatalogView`)
// sin duplicar la lógica de MediaRecorder + Web Audio.
//
// Vías de conversión (una fallback de la otra, ver `audioPcm.ts`):
//   1. captura PCM EN VIVO desde el micrófono (principal, fiable en Safari/iOS),
//   2. decodificación del blob del MediaRecorder (fallback).

import { useCallback, useRef, useState } from 'react';
import { i18n } from '../i18n/index.js';
import { startPcmCapture, decodeBlobToPcm, type PcmCapture } from './audioPcm.js';

/** Estados del ciclo de captura. */
export type AudioClipState = 'idle' | 'recording' | 'processing';

/** Duración máxima de la grabación por defecto (ms). */
const DEFAULT_MAX_MS = 7000;

/** API del hook de captura de audio. */
export interface UseAudioClip {
  /** Estado actual del ciclo. */
  state: AudioClipState;
  /** Mensaje de error de captura (permiso de micrófono, etc.), o `null`. */
  error: string | null;
  /**
   * Inicia la grabación. Resuelve con el PCM base64 cuando termina (por tiempo o
   * por `stop()`), o `null` si no se pudo preparar audio utilizable. Nunca lanza.
   */
  start(): Promise<string | null>;
  /** Detiene la grabación en curso (dispara la resolución de `start`). */
  stop(): void;
}

/**
 * Hook de captura de un clip de audio y su conversión a PCM base64.
 *
 * @param maxMs duración máxima de la grabación en ms (por defecto 7000).
 */
export function useAudioClip(maxMs: number = DEFAULT_MAX_MS): UseAudioClip {
  const [state, setState] = useState<AudioClipState>('idle');
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const pcmCaptureRef = useRef<PcmCapture | null>(null);
  const stopTimerRef = useRef<number | null>(null);
  // Resolver de la promesa que `start()` devolvió, para completarla desde `onstop`.
  const resolveRef = useRef<((value: string | null) => void) | null>(null);

  /** Convierte lo capturado a PCM base64: PCM en vivo y, si no, el blob. */
  const buildPcmBase64 = useCallback(async (): Promise<string | null> => {
    const capture = pcmCaptureRef.current;
    pcmCaptureRef.current = null;
    if (capture) {
      try {
        const live = await capture.stop();
        if (live) return live;
      } catch {
        /* cae al fallback */
      }
    }
    if (chunksRef.current.length > 0) {
      const blob = new Blob(chunksRef.current, {
        type: recorderRef.current?.mimeType || 'audio/webm',
      });
      try {
        return await decodeBlobToPcm(blob);
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  const stop = useCallback(() => {
    if (stopTimerRef.current !== null) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  }, []);

  const start = useCallback(async (): Promise<string | null> => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      try {
        pcmCaptureRef.current = startPcmCapture(stream);
      } catch {
        pcmCaptureRef.current = null;
      }
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      const done = new Promise<string | null>((resolve) => {
        resolveRef.current = resolve;
      });

      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setState('processing');
        void buildPcmBase64().then((base64) => {
          setState('idle');
          resolveRef.current?.(base64);
          resolveRef.current = null;
        });
      };

      recorder.start();
      setState('recording');
      stopTimerRef.current = window.setTimeout(stop, maxMs);
      return await done;
    } catch {
      setError(i18n.t('common.micError'));
      setState('idle');
      return null;
    }
  }, [buildPcmBase64, stop, maxMs]);

  return { state, error, start, stop };
}
